import { memo, useCallback, PropsWithChildren } from 'react';
import { shallow } from './shallowComparison.utils';
import {
	ControlProps,
	Panel,
	ReactFlowState,
	useReactFlow,
	useStore,
	useStoreApi,
} from '@xyflow/react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import styled from 'styled-components';
import { StyledControlButton } from './StyledControlButton';
import { Divider, Flex, rem, FlexProps } from '@mantine/core';

const cc = (classNames?: Array<string | undefined>) => classNames?.filter((s) => s).join(' ');

const selector = (s: ReactFlowState) => ({
	isInteractive: s.nodesDraggable || s.nodesConnectable || s.elementsSelectable,
	minZoomReached: s.transform[2] <= s.minZoom,
	maxZoomReached: s.transform[2] >= s.maxZoom,
});

function PreReactFlowControls({
	style,
	showZoom = true,
	showFitView = true,
	showInteractive = true,
	showSave = true,
	showReset = true,
	onSave,
	onReset,
	fitViewOptions,
	onZoomIn,
	onZoomOut,
	onFitView,
	onInteractiveChange,
	className,
	position = 'bottom-left',
	orientation = 'vertical',
	'aria-label': ariaLabel = 'React Flow controls',
	children,
}: PropsWithChildren<
	ControlProps & {
		showSave?: boolean;
		showReset?: boolean;
		onSave?: () => void;
		onReset?: () => void;
	}
>) {
	const store = useStoreApi();
	const { isInteractive, minZoomReached, maxZoomReached } = useStore(selector, shallow);
	const { zoomIn, zoomOut, fitView } = useReactFlow();

	const onZoomInHandler = () => {
		void zoomIn();
		onZoomIn?.();
	};

	const onZoomOutHandler = () => {
		void zoomOut();
		onZoomOut?.();
	};

	const onFitViewHandler = () => {
		void fitView(fitViewOptions);
		onFitView?.();
	};

	const onToggleInteractivity = useCallback(() => {
		if (
			store.getState().nodesDraggable === !isInteractive &&
			store.getState().nodesConnectable === !isInteractive &&
			store.getState().elementsSelectable === !isInteractive
		) {
			return;
		}

		store.setState({
			nodesDraggable: !isInteractive,
			nodesConnectable: !isInteractive,
			elementsSelectable: !isInteractive,
		});

		onInteractiveChange?.(!isInteractive);
	}, [isInteractive, onInteractiveChange]);

	const orientationClass = orientation === 'horizontal' ? 'horizontal' : 'vertical';

	return (
		<StyledControlPanel
			className={cc(['react-flow__controls', orientationClass, className])}
			position={position}
			style={style}
			data-testid="rf__controls"
			aria-label={ariaLabel}
		>
			<StyledControlsContainer direction="column">
				{showZoom && (
					<>
						<StyledControlButton
							onClick={onZoomInHandler}
							className="react-flow__controls-zoomin"
							title="zoom in"
							aria-label="zoom in"
							disabled={maxZoomReached}
						>
							<Icon variant="zoom-in" size="xl" />
						</StyledControlButton>
						<StyledControlButton
							onClick={onZoomOutHandler}
							className="react-flow__controls-zoomout"
							title="zoom out"
							aria-label="zoom out"
							disabled={minZoomReached}
						>
							<Icon variant="zoom-out" size="xl" />
						</StyledControlButton>
					</>
				)}
				{showFitView && (
					<StyledControlButton
						className="react-flow__controls-fitview"
						onClick={onFitViewHandler}
						title="fit view"
						aria-label="fit view"
					>
						<Icon variant="resize" size="xl" />
					</StyledControlButton>
				)}
				<Divider />
				{showSave && (
					<StyledControlButton onClick={onSave} title="save" aria-label="save">
						<Icon variant="save" size="xl" />
					</StyledControlButton>
				)}
				{showInteractive && (
					<StyledControlButton
						className="react-flow__controls-interactive"
						onClick={onToggleInteractivity}
						title="toggle interactivity"
						aria-label="toggle interactivity"
					>
						{isInteractive ? (
							<Icon variant="lock-open" size="xl" />
						) : (
							<Icon variant="lock" size="xl" />
						)}
					</StyledControlButton>
				)}
				{showReset && (
					<StyledControlButton onClick={onReset} title="reset" aria-label="reset">
						<Icon variant="refresh-v3" size="xl" />
					</StyledControlButton>
				)}
			</StyledControlsContainer>
			{children}
		</StyledControlPanel>
	);
}

const StyledControlPanel = styled(Panel)`
	&.react-flow__panel {
		border: none;
		box-shadow: none;
		background-color: transparent;
	}
`;

const StyledControlsContainer = styled(Flex)<FlexProps>`
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	box-shadow: none;
	border-radius: ${rem('8px')};
	overflow: hidden;

	.mantine-Divider-root {
		border-top-color: ${({ theme }) => theme.v3.colors.gray[2]};
	}
`;

export const ReactFlowControls = memo(PreReactFlowControls);
